import React, { useCallback, useState, useRef } from 'react';
import ReactPlayer from 'react-player';
import {
    PlayCircleOutlined,
    PauseCircleOutlined,
    ForwardOutlined,
    BackwardOutlined,
    FallOutlined,
} from '@ant-design/icons';
import * as S from './styles';
import { convertToPlus3Timezone } from '@utils/functions/convertDate';

interface VideoPlayerProps {
    source: string;
    date: string;
    phone: string;
}

export const CustomPlayer = ({ source, phone, date }: VideoPlayerProps) => {
    const playerRef = useRef<HTMLVideoElement | null>(null);
    const [playing, setPlaying] = useState(false);
    const [volume, setVolume] = useState(0.5);
    const [played, setPlayed] = useState(0);

    const handlePlayPause = () => {
        setPlaying(p => !p);
    };

    const seekForward = () => {
        const player = playerRef.current;
        if (!player) return;
        player.currentTime = Math.min(player.currentTime + 10, player.duration);
    };

    const seekBackward = () => {
        const player = playerRef.current;
        if (!player) return;
        player.currentTime = Math.max(player.currentTime - 10, 0);
    };

    const progressHandler = () => {
        const player = playerRef.current;
        if (!player || !player.duration) return;
        setPlayed(player.currentTime / player.duration);
    };

    const setPlayerRef = useCallback((player: HTMLVideoElement | null) => {
        if (player) {
            playerRef.current = player;
        }
    }, []);

    return (
        <S.Container>
            <ReactPlayer
                ref={setPlayerRef}
                src={source}
                playing={playing}
                volume={volume}
                onTimeUpdate={progressHandler}
                controls={false}
                width="100%"
                height="0"
            />
            <S.ControlsContainer>
                <S.ControlButton onClick={handlePlayPause}>
                    {playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                </S.ControlButton>
                <S.BtnContainer>
                    <S.ControlButton onClick={seekBackward}>
                        <BackwardOutlined />
                    </S.ControlButton>
                    <S.ControlButton onClick={seekForward}>
                        <ForwardOutlined />
                    </S.ControlButton>
                </S.BtnContainer>
                <S.InfoContainer>
                    <FallOutlined />
                    <span>{phone}</span>
                    <span style={{ color: '#b0b0b0' }}>
                        {convertToPlus3Timezone(date)}
                    </span>
                </S.InfoContainer>
            </S.ControlsContainer>

            <S.ControlGroup>
                <S.ControlLabel htmlFor="volume">Volume:</S.ControlLabel>
                <S.StyledSlider
                    id="volume"
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    onChange={e => setVolume(parseFloat(e.target.value))}
                    value={volume}
                />
                <S.VolumeValue>{Math.round(volume * 100)}%</S.VolumeValue>
            </S.ControlGroup>

            <S.ControlGroup>
                <S.ControlLabel htmlFor="progress">Progress:</S.ControlLabel>
                <S.StyledSlider
                    id="progress"
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    onChange={e => {
                        const newValue = parseFloat(e.target.value);
                        setPlayed(newValue);

                        const player = playerRef.current;
                        if (!player || !player.duration) return;

                        player.currentTime = newValue * player.duration;
                    }}
                    value={played}
                />
                <S.ProgressValue>{Math.round(played * 100)}%</S.ProgressValue>
            </S.ControlGroup>
        </S.Container>
    );
};
